import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ThunderboltOutlined, 
  PlusOutlined,
  ReloadOutlined,
  FilterOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { actionApi } from '../../utils/actionApi';
import { getSuccessMessage } from '../../utils/actionMessageTranslator';
import { message, Spin, Input, Button, Select, Card, Row, Col, InputNumber, Space, Popconfirm, Tooltip } from 'antd';
import { StatusBadge, PriorityBadge, ProgressBadge, ActionCard, FilterPanel } from '../../components/action';
import { CardListSkeleton } from '../../components/SkeletonLoaders';

const Action = () => {
  const navigate = useNavigate();
  const { actionToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    action_type: '',
    source_module: '',
    requester_id: '',
    assigned_to: '',
    raci_role: '',
    raci_user_id: '',
    search: '',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  useEffect(() => {
    if (actionToken) {
      fetchActions(1);
    }
  }, [actionToken]);

  const fetchActions = async (page = 1) => {
    if (!actionToken) return;

    setLoading(true);
    try {
      const res = await actionApi.getActions({
        page,
        per_page: pagination.per_page,
        ...filters,
      });
      const list = res?.data?.data || [];
      const pag = res?.data?.pagination || pagination;
      setActions(list);
      setPagination(pag);
    } catch (error) {
      console.error('Load actions error:', error);
      message.error('خطا در دریافت لیست اقدام‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchActions(pagination.current_page || 1);
  };

  const handleCardClick = (action) => {
    navigate(`/action/${action.id}`);
  };

  const handleDelete = async (id) => {
    try {
      const res = await actionApi.deleteAction(id);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام حذف شد'));
        fetchActions(pagination.current_page || 1);
      } else {
        message.error(res?.message || 'خطا در حذف اقدام');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف اقدام');
    }
  };

  // استفاده از کامپوننت‌های جدید - دیگر نیازی به renderStatusBadge، renderPriorityBadge و renderPercent نیست

  const isEmpty = !loading && (!actions || actions.length === 0);

  const actionTypes = [
    { value: '', label: 'همه نوع‌ها' },
    { value: 'اصلاح', label: 'اصلاح' },
    { value: 'اقدام_پیشگیرانه', label: 'اقدام پیشگیرانه' },
    { value: 'اقدام_اصلاحی', label: 'اقدام اصلاحی' },
  ];

  const statuses = [
    { value: '', label: 'همه وضعیت‌ها' },
    { value: 'باز', label: 'باز' },
    { value: 'در_حال_برنامه_ریزی', label: 'در حال برنامه‌ریزی' },
    { value: 'در_حال_انجام', label: 'در حال انجام' },
    { value: 'بسته', label: 'بسته' },
  ];

  const priorities = [
    { value: '', label: 'همه اولویت‌ها' },
    { value: 'فوری_و_اضطراری', label: 'فوری و اضطراری' },
    { value: 'بسیار_مهم', label: 'بسیار مهم' },
    { value: 'مهم', label: 'مهم' },
    { value: 'کم_اهمیت', label: 'کم اهمیت' },
    { value: 'غیرضروری', label: 'غیرضروری' },
  ];

  const raciRoles = [
    { value: '', label: 'همه نقش‌ها' },
    { value: 'R', label: 'R' },
    { value: 'A', label: 'A' },
    { value: 'C', label: 'C' },
    { value: 'I', label: 'I' },
  ];

  const perPageOptions = [10, 15, 20, 30, 50];

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // فیلتر خودکار با debounce
  useEffect(() => {
    if (!actionToken) return;
    const timer = setTimeout(() => {
      fetchActions(1);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const resetFilters = () => {
    setFilters({
      status: '',
      priority: '',
      action_type: '',
      source_module: '',
      requester_id: '',
      assigned_to: '',
      raci_role: '',
      raci_user_id: '',
      search: '',
    });
  };

  return (
    <MainLayout>
      <div className="px-4 md:px-6 py-5 w-full">
        {/* هدر پیشرفته */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <ThunderboltOutlined className="text-white text-xl" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900" />
              </div>
              <div>
                <h1
                  className="text-2xl font-bold text-gray-900 dark:text-white"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  اقدام‌ها
                </h1>
                <p
                  className="text-sm text-gray-500 dark:text-gray-400 mt-0.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  {pagination.total > 0 ? `${pagination.total} اقدام فعال` : 'مدیریت کارهای اقدامی'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center gap-2 transition-colors"
                style={{ fontFamily: 'CustomFont' }}
              >
                <ReloadOutlined className="text-sm" />
                به‌روزرسانی
              </button>
              <button
                onClick={() => navigate('/action/create')}
                className="px-5 py-2 text-sm rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 transition-all"
                style={{ fontFamily: 'CustomFont' }}
              >
                <PlusOutlined className="text-sm" />
                اقدام جدید
              </button>
            </div>
          </div>

          {/* استفاده از کامپوننت FilterPanel */}
          <FilterPanel
            filters={filters}
            onFilterChange={(key, value) => setFilter(key, value)}
            onApply={() => {}}
            onReset={resetFilters}
            filterOptions={{
              statuses,
              priorities,
              actionTypes,
              raciRoles,
              perPageOptions,
            }}
            defaultExpanded={filtersOpen}
            className="mb-6"
          />

        </div>

        {/* بدنه لیست */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          {loading && (
            <div className="p-6">
              <CardListSkeleton count={5} />
            </div>
          )}

          {!loading && isEmpty && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="relative mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center">
                  <ThunderboltOutlined className="text-gray-400 text-2xl" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">!</span>
                </div>
              </div>
              <p
                className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1"
                style={{ fontFamily: 'CustomFont' }}
              >
                {filters.search ? 'نتیجه‌ای یافت نشد' : 'هنوز هیچ اقدامی ثبت نشده است'}
              </p>
              <p
                className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md"
                style={{ fontFamily: 'CustomFont' }}
              >
                {filters.search 
                  ? 'لطفاً عبارت جستجوی دیگری را امتحان کنید'
                  : 'برای شروع، یک اقدام جدید بسازید و آن را به یکی از ماژول‌ها متصل کنید'}
              </p>
              {!filters.search && (
                <button
                  onClick={() => navigate('/action/create')}
                  className="px-5 py-2.5 text-sm rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white flex items-center gap-2 shadow-lg shadow-emerald-500/25 transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  <PlusOutlined className="text-sm" />
                  ساخت اقدام جدید
                </button>
              )}
            </div>
          )}

          {!loading && !isEmpty && (
            <div className="space-y-3">
              {actions.map((action, index) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  onClick={handleCardClick}
                  onDelete={(action) => handleDelete(action.id)}
                  className="animate-fadeIn"
                  style={{
                    animationDelay: `${index * 0.05}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* صفحه‌بندی */}
        {!loading && !isEmpty && pagination.last_page > 1 && (
          <div className="mt-6">
            <div className="flex items-center justify-center">
              <div className="max-w-full overflow-x-auto">
                <div className="inline-flex items-center justify-center gap-2 px-1">
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => fetchActions(page)}
                      className={`w-10 h-10 rounded-xl text-sm font-medium transition-all flex items-center justify-center ${
                        page === pagination.current_page
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                          : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </MainLayout>
  );
};

export default Action;